import React, { useMemo } from 'react'
import { BottomSheet, Button, Separator, Typography } from '@s4l/ds'
import { IconInfo } from '@s4l/icons/IconInfo'
import styles from './styles.module.css'
import PaymentRow from './PaymentRow'
import RoomSection from './RoomSection'
import { type RoomData, PAYMENT_DISPLAY_TYPES } from './types'
import {
  groupTaxDetails,
  getNotIncludedTaxCurrency,
  getAllTotalsByPayment,
  calculateAggregatedTotals,
} from './utils'
import { usePriceDetailsHelpers } from './hooks'

export interface PriceDetailsProps {
  nights: number
  rooms: RoomData[]
  variant?: 'bottomsheet' | 'inline'
}

function PriceDetailsContent({ nights, rooms }: { nights: number; rooms: RoomData[] }) {
  const { formatCurrency, formatMessage, formatMessageCapitalized, formatDate } =
    usePriceDetailsHelpers()

  // Calculate aggregated data from rooms array
  const aggregatedData = useMemo(() => {
    if (!rooms || rooms.length === 0) {
      return null
    }
    return calculateAggregatedTotals(rooms)
  }, [rooms])

  const groupedTaxDetails = useMemo(
    () => (aggregatedData?.taxDetails ? groupTaxDetails(aggregatedData.taxDetails) : []),
    [aggregatedData?.taxDetails]
  )

  const paymentTotals = useMemo(() => getAllTotalsByPayment(groupedTaxDetails), [groupedTaxDetails])

  const payAtPropertyCurrency = useMemo(
    () =>
      aggregatedData?.taxDetails ? getNotIncludedTaxCurrency(aggregatedData.taxDetails) : undefined,
    [aggregatedData?.taxDetails]
  )

  const localCurrencyText = useMemo(() => {
    const baseText = formatMessage({ id: 'global.local-currency' })
    const paymentText = payAtPropertyCurrency
      ? formatMessage(
          { id: 'tax-breakdown.payment-at-property' },
          { currency: payAtPropertyCurrency }
        )
      : ''
    return ` ${baseText} ${paymentText}`
  }, [formatMessage, payAtPropertyCurrency])

  const renderPaymentsByDate = () => (
    <>
      {aggregatedData?.paymentsByDate?.map((payment, index) => (
        <PaymentRow
          key={payment.date || `payment-${index}`}
          label={formatMessageCapitalized(
            { id: 'policies.pay-on' },
            { date: formatDate(payment.date) }
          )}
          amount={payment.amount}
          currency={payment.currency}
        />
      ))}
    </>
  )

  const renderPayNowRow = (amount: number, currency: string) => (
    <PaymentRow
      label={formatMessage({ id: 'global.pay-now' })}
      amount={amount}
      currency={currency}
    />
  )

  if (!aggregatedData || !nights || nights <= 0) {
    return null
  }

  const {
    subtotal,
    subtotalCurrency,
    totalCurrency,
    payDate,
    hasInsurance,
    insuranceAmount,
    insuranceCurrency,
    totalWithInsurance,
    payNowWithInsurance,
    payLate,
    total,
    roomsNumber,
    isMultiRoom,
    paymentDisplayType,
  } = aggregatedData

  if (!subtotal || !subtotalCurrency || !roomsNumber) {
    return null
  }

  const formattedPayDate = payDate ? formatDate(payDate) : undefined
  const hasPayAtHotelTaxes = paymentTotals.payLater > 0

  return (
    <>
      <div className={styles.section}>
        <>
          {rooms.map((room, index) => (
            <RoomSection
              key={`room-${index + 1}`}
              room={room}
              index={index}
              nights={nights}
              isMultiRoom={isMultiRoom}
            />
          ))}

          {hasInsurance && (
            <div className={styles.insuranceRow}>
              <Typography variant="body" size="l">
                {formatMessage({ id: 'insurance.refund-protect' })}
              </Typography>
              <Typography variant="body" size="l">
                {formatCurrency(insuranceAmount, insuranceCurrency)}
              </Typography>
            </div>
          )}
        </>
      </div>

      <Separator className={styles.separator} />

      <div className={styles.section}>
        <div className={styles.totalHeader}>
          <Typography variant="title" size="xs" bold>
            {formatMessage({ id: 'global.total' })} ({totalCurrency})
          </Typography>

          <Typography variant="title" size="xs" bold>
            {formatCurrency(totalWithInsurance, totalCurrency)}
          </Typography>
        </div>

        <div className={styles.subHeader}>
          <Typography variant="caption" size="l">
            {`${formatMessage({ id: 'global.nights' }, { nights })}, ${formatMessage({ id: 'global.rooms-plural' }, { rooms: roomsNumber })}`}
          </Typography>
          <Typography variant="caption" size="l">
            {formatMessage({ id: 'global.taxes-fees-reduced' })}
          </Typography>
        </div>

        <div className={styles.detailsBlockDark}>
          {(() => {
            // For multi-room scenarios, always use the default case to show individual payment dates
            if (isMultiRoom) {
              return (
                <>
                  {payNowWithInsurance > 0 && renderPayNowRow(payNowWithInsurance, totalCurrency)}
                  {renderPaymentsByDate()}
                </>
              )
            }

            switch (paymentDisplayType) {
              case PAYMENT_DISPLAY_TYPES.NON_REFUNDABLE:
                return renderPayNowRow(payNowWithInsurance, totalCurrency)

              case PAYMENT_DISPLAY_TYPES.PAY_ON_ARRIVAL_ONLY:
                return (
                  <PaymentRow
                    label={formatMessageCapitalized(
                      { id: 'policies.pay-on' },
                      { date: formattedPayDate }
                    )}
                    amount={total}
                    currency={totalCurrency}
                  />
                )

              case PAYMENT_DISPLAY_TYPES.PAY_UPON_BOOKING_WITH_LATER_PAYMENT:
                return (
                  <PaymentRow
                    label={formatMessageCapitalized({ id: 'tax-breakdown.pay-upon-booking' })}
                    amount={payNowWithInsurance}
                    currency={totalCurrency}
                  />
                )

              case PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT:
                return (
                  <>
                    <PaymentRow
                      label={`${formatMessage({ id: 'global.pay-now' })} (${formatMessage({ id: 'policies.deposit' })})`}
                      amount={payNowWithInsurance}
                      currency={totalCurrency}
                    />
                    <PaymentRow
                      label={formatMessageCapitalized(
                        { id: 'policies.pay-on' },
                        { date: formattedPayDate }
                      )}
                      amount={payLate ?? undefined}
                      currency={totalCurrency}
                    />
                  </>
                )

              default:
                return (
                  <>
                    {payNowWithInsurance > 0 && renderPayNowRow(payNowWithInsurance, totalCurrency)}
                    {renderPaymentsByDate()}
                  </>
                )
            }
          })()}

          {hasPayAtHotelTaxes && (
            <PaymentRow
              label={formatMessageCapitalized({ id: 'tax-breakdown.pay-at-hotel' })}
              amount={paymentTotals.payLater}
              currency={paymentTotals.currency}
              withTaxDetails
              taxDetails={groupedTaxDetails}
            />
          )}
        </div>
      </div>

      <div className={styles.content}>
        <Typography variant="caption" size="l" bold>
          {`${formatMessage({ id: 'global.no-conversion-fees' })}`}
        </Typography>
        <Typography variant="caption" size="l">
          {localCurrencyText}
        </Typography>
      </div>
    </>
  )
}

export default function PriceDetails({
  nights,
  rooms,
  variant = 'bottomsheet',
}: Readonly<PriceDetailsProps>) {
  const { formatMessage } = usePriceDetailsHelpers()

  // Early validation for both variants
  if (!rooms || rooms.length === 0 || !nights || nights <= 0) {
    return null
  }

  // If variant is inline, render content directly with title
  if (variant === 'inline') {
    return (
      <>
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <Typography variant="title" size="xs" bold>
            {formatMessage({ id: 'global.price-breakdown' })}
          </Typography>
        </div>
        <PriceDetailsContent nights={nights} rooms={rooms} />
      </>
    )
  }

  // Default bottomsheet behavior
  return (
    <BottomSheet>
      <BottomSheet.Trigger asChild>
        <Button variant="icon-flat" size="extraSmall" leadingIcon={<IconInfo />} />
      </BottomSheet.Trigger>
      <BottomSheet.Content
        title={formatMessage({ id: 'global.price-breakdown' })}
        modalOnDesktop
        size="s"
      >
        <PriceDetailsContent nights={nights} rooms={rooms} />
      </BottomSheet.Content>
    </BottomSheet>
  )
}
